import React from 'react';
import { useTranslation } from 'react-i18next';
import { Col, FormGroup, Label, Row } from 'reactstrap';

import customerApi from 'api/contacts';
import LinkButton from 'components/layout/buttons/LinkButton';
import SearchDropdown from 'components/layout/forms/SearchDropdown';
import SelectInputFieldControlled from 'components/layout/forms/SelectInputFieldControlled';
import TrashIcon from 'components/layout/icons/TrashIcon';
import translations from 'services/translations/translations';
import { showDesigners } from 'components/offers/OfferSelectOptions';

export interface IRecipientRow {
  key: number;
  office: { label: string; value: string } | null;
  personId: string;
  persons: any[];
}

const loadPersons = async (officeId: string): Promise<any[]> => {
  try {
    const contact = await customerApi.getContact(officeId, {
      ownOrganizationUnit: true,
    });
    return contact.contactPersons ?? [];
  } catch (error) {
    return [];
  }
};

// Builds the rows of the additional recipients from the saved customer contacts
export const getRecipientRows = async (
  customerContacts: {
    contact: { id: number; companyName: string };
    contactPerson: { id: number } | null;
  }[]
): Promise<IRecipientRow[]> =>
  Promise.all(
    customerContacts.map(async (customerContact, index) => ({
      key: index + 1,
      office: {
        label: customerContact.contact.companyName,
        value: customerContact.contact.id.toString(),
      },
      personId: customerContact.contactPerson?.id?.toString() ?? '',
      persons: await loadPersons(customerContact.contact.id.toString()),
    }))
  );

/**
 * A preliminary offer is usually sent to several companies and contact persons. The main
 * customer is chosen in the customer fields, the other recipients are listed here.
 */
const AdditionalRecipients = ({
  locked,
  rows,
  searchCustomerOffice,
  setRows,
}: {
  locked: boolean;
  rows: IRecipientRow[];
  searchCustomerOffice: (inputValue: string) => Promise<any>;
  setRows: (rows: IRecipientRow[]) => void;
}): JSX.Element => {
  const { t } = useTranslation([translations.offers, translations.common]);

  const updateRow = (key: number, changes: Partial<IRecipientRow>) =>
    setRows(
      rows.map((row) => (row.key === key ? { ...row, ...changes } : row))
    );

  const changeOffice = async (
    key: number,
    office: { label: string; value: string } | null
  ) => {
    updateRow(key, { office, personId: '', persons: [] });

    if (office !== null && office.value !== '') {
      const persons = await loadPersons(office.value);
      setRows(
        rows.map((row) =>
          row.key === key ? { ...row, office, personId: '', persons } : row
        )
      );
    }
  };

  const addRow = () =>
    setRows([
      ...rows,
      {
        key: Math.max(0, ...rows.map((row) => row.key)) + 1,
        office: null,
        personId: '',
        persons: [],
      },
    ]);

  const removeRow = (key: number) =>
    setRows(rows.filter((row) => row.key !== key));

  return (
    <div className="additional-recipients">
      <Label className="label">
        {t(`${translations.offers}:Other recipients`)}
      </Label>
      <p className="additional-recipients-info">
        {t(`${translations.offers}:Other recipients info`)}
      </p>
      {rows.map((row) => (
        <Row key={row.key}>
          <Col md={6}>
            <FormGroup>
              <SearchDropdown
                value={row.office}
                placeholder={t(`${translations.common}:Search`)}
                className="text-field search-field"
                name={`recipient-office-${row.key}`}
                onChange={(office: any) => changeOffice(row.key, office)}
                loadOptions={searchCustomerOffice}
                disabled={locked}
              />
            </FormGroup>
          </Col>
          <Col md={5}>
            <SelectInputFieldControlled
              value={row.personId}
              id={`recipient-person-${row.key}`}
              name={`recipient-person-${row.key}`}
              onChange={(event: any) =>
                updateRow(row.key, { personId: event.target.value })
              }
              options={showDesigners(row.persons)}
              disabled={locked || row.office === null}
            />
          </Col>
          <Col md={1}>
            {locked ? null : (
              <TrashIcon
                style={{ marginTop: '0.5rem' }}
                onClick={() => removeRow(row.key)}
              />
            )}
          </Col>
        </Row>
      ))}
      {locked ? null : (
        <LinkButton onClick={addRow}>
          {`+ ${t(`${translations.offers}:Add recipient`)}`}
        </LinkButton>
      )}
    </div>
  );
};

export default AdditionalRecipients;
